import { type MotionValue, easeInOut, useScroll, useTransform } from "framer-motion";
import { type RefObject, useRef } from "react";

/** First row finishes collapsing / second row finishes expanding. */
export const SECTION_TWO_FIRST_HANDOFF = 1 / 3;
/** Last row is fully open; remaining progress is the S3 cover pin-hold. */
export const SECTION_TWO_ACCORDION_SETTLE = 2 / 3;

/**
 * Scroll ranges for the S2 accordion: row 1 starts expanded, then each scroll segment
 * collapses the open row while expanding the next. Outputs across all rows always sum to
 * 1, so the pinned stage stays exactly viewport-height through every handoff.
 *
 * The runway is 400svh. Framer progress is height minus one viewport, so 0–1 covers
 * three scroll viewports: two handoffs, then a 100svh pin-hold. Ranges stay on that
 * 0–1 scale so shrinking the pinned stage below the site header does not move the
 * handoff points. There is no dwell after a row reaches flexGrow 1 — the next row
 * starts on the following scroll tick. The accordion plays over the first two thirds;
 * the final third is the pin-hold while S3 slides over it.
 */
export const SECTION_TWO_ACCORDION_RANGES = [
	{ input: [0, SECTION_TWO_FIRST_HANDOFF], output: [1, 0] },
	{ input: [0, SECTION_TWO_FIRST_HANDOFF, SECTION_TWO_ACCORDION_SETTLE], output: [0, 1, 0] },
	{ input: [SECTION_TWO_FIRST_HANDOFF, SECTION_TWO_ACCORDION_SETTLE, 1], output: [0, 1, 1] },
] as const;

export type TSectionTwoAccordion = {
	runwayRef: RefObject<HTMLDivElement | null>;
	grows: MotionValue<number>[];
};

/**
 * Drives the desktop S2 accordion. A 400svh runway pins the stage below the site header;
 * scroll progress flexes each row's body open or closed, one at a time, then holds the
 * settled stage for the final viewport while S3 covers it.
 */
export function useSectionTwoAccordion(): TSectionTwoAccordion {
	const runwayRef = useRef<HTMLDivElement>(null);
	const { scrollYProgress } = useScroll({ target: runwayRef, offset: ["start start", "end end"] });
	const first = useTransform(
		scrollYProgress,
		[...SECTION_TWO_ACCORDION_RANGES[0].input],
		[...SECTION_TWO_ACCORDION_RANGES[0].output],
		{
			ease: easeInOut,
		},
	);
	const second = useTransform(
		scrollYProgress,
		[...SECTION_TWO_ACCORDION_RANGES[1].input],
		[...SECTION_TWO_ACCORDION_RANGES[1].output],
		{
			ease: easeInOut,
		},
	);
	const third = useTransform(
		scrollYProgress,
		[...SECTION_TWO_ACCORDION_RANGES[2].input],
		[...SECTION_TWO_ACCORDION_RANGES[2].output],
		{
			ease: easeInOut,
		},
	);
	return { runwayRef, grows: [first, second, third] };
}
